/**
 * Post-purchase copy, transcribed from the CMO-authored click-through
 * (Bambee_ClickThrough_From_Payment_v3_2026-10-02.html) with no edits.
 * Trademarks in it still need the sign-off flagged in BIO-1643.
 *
 * Copy lives here rather than in templates so marketing can revise wording
 * without touching component code, as modules/HomePage/bside/constants does.
 * Strings with <em> are rendered with v-html; they are static, not user input.
 */
import { SupervisionLevel } from '@/constants/Experiments';

export const PostPurchaseSteps = [
  'How much you hand to Bambee',
  'Choose your plan',
  'Bambee installation',
];

// Who hosts the founder installation call. Team round-robin events, not a named
// advisor: a brand-new self-serve company has no advisor assigned yet (BIO-1777).
export const FounderCallHost = {
  [SupervisionLevel.OBSERVATION]: 'Coverage Advisor',
  [SupervisionLevel.SUPERVISION]: 'Chief of Staff',
  [SupervisionLevel.MANAGEMENT]: 'Chief of Staff',
};

// Calendly event links per host, from the Netlify context (Sales/Success supply them).
// Empty means "not configured yet": the schedule page then shows the contact fallback.
export const FounderCallEventUrl = {
  [SupervisionLevel.OBSERVATION]: process.env.CALENDLY_FOUNDER_INSTALL_COVERAGE,
  [SupervisionLevel.SUPERVISION]: process.env.CALENDLY_FOUNDER_INSTALL_COS,
  [SupervisionLevel.MANAGEMENT]: process.env.CALENDLY_FOUNDER_INSTALL_COS,
};

export const Installation = {
  kick: 'BAMBEE INSTALLATION',
  title: 'Here’s how we install <em>Bambee</em> inside your business.',
  sub: 'Three steps to get your team set up, then Bambee runs every month.',
  cta: 'Set up your founder installation call',
  steps: [
    {
      title: 'Founder installation call',
      body: '<b>Your first call with Bambee:</b> we walk you through how Bambee runs inside your business, step by step, before your managers and employees are brought in.',
    },
    {
      title: 'Manager installation',
      body: '<b>Your managers get set up:</b> we meet with your managers, collect their signatures on every policy, and set expectations for their role: their coaching sessions with Bambee and monthly feedback on their employees.',
    },
    {
      title: 'Employee installation',
      body: '<b>Your employees get set up:</b> we meet with your employees and collect their signatures on every policy. Bambee can’t run without full policy acknowledgement.',
    },
  ],
};

export const Schedule = {
  kick: 'FOUNDER INSTALLATION CALL',
  title: 'Schedule your <em>founder installation</em> call.',
  sub: (host) =>
    `Pick a time for your first call with your ${host}. We’ll walk you through how Bambee runs inside your business, step by step.`,
  fallback:
    'Online scheduling isn’t available right now. Reach our team by phone or email and we’ll set up your founder installation call.',
  later: 'I’ll schedule later',
};

// Hand-off questions. Q1 "Nothing" goes straight to Observation; "All of it" asks Q2,
// whose answers are the Supervision and Management levels. Icons are the comp's SVG
// inner markup, rendered into a 24x24 viewBox.
export const Handoff = {
  tag: 'PLAN SELECTION',
  sideTitle: 'The right plan starts with how you want to run HR.',
  sidePoints: [
    '<b>It shapes every week with Bambee:</b> what we do inside your company, and how often you hear from us.',
    '<b>There is no wrong answer:</b> the right fit is the one that matches how you want to run your business today.',
    '<b>It keeps the choice simple:</b> you only see the plans built for the way you work.',
  ],
  questions: [
    {
      title: 'How much do you want Bambee to take off your plate?',
      options: [
        {
          value: 'all',
          answer: 'All of it.',
          text: 'I want the work off my plate. I want to know Bambee is finding the biggest problems and making sure they’re solved before they ever reach my desk.',
          icon: '<path d="M4 12l4 4 8-9"/><path d="M12 16l2 2 7-8"/>',
        },
        {
          value: 'none',
          answer: 'Nothing.',
          text: 'I want to own HR for my company end to end. I just want an expert on call to talk through the stickiest situations.',
          icon: '<circle cx="12" cy="8" r="3.6"/><path d="M5 20c.8-3.6 3.6-5.6 7-5.6s6.2 2 7 5.6"/>',
        },
      ],
    },
    {
      title:
        'Great. The next time an HR issue comes up, who do you want running it?',
      options: [
        {
          value: SupervisionLevel.MANAGEMENT,
          answer: 'My Bambee HR Manager runs it for me.',
          text: 'My Bambee HR Manager resolves every HR issue for me, start to finish. Bambee meets with my team every month to catch problems early, and every issue is certified complete, so I have proof it was done right.',
          icon: '<circle cx="12" cy="9" r="3.4"/><path d="M5.5 20c.8-3.4 3.4-5.2 6.5-5.2s5.7 1.8 6.5 5.2"/><path d="M7 9a5 5 0 0 1 10 0"/><path d="M17 9v1.6"/><path d="M7 9v1.6"/>',
        },
        {
          value: SupervisionLevel.SUPERVISION,
          answer: 'My managers run it, with Bambee oversight.',
          text: 'My managers handle the work themselves. In regular manager check-ins, Bambee flags problem behavior, gives my managers a step-by-step plan for the work, and holds them accountable to finish it by the next check-in.',
          icon: '<circle cx="8" cy="9" r="3"/><circle cx="16.5" cy="9" r="3"/><path d="M2.5 19c.6-2.8 2.8-4.4 5.5-4.4s4.9 1.6 5.5 4.4"/><path d="M11.5 19c.6-2.8 2.4-4.4 5-4.4s4.4 1.6 5 4.4"/>',
        },
      ],
    },
  ],
  back: '‹ Change my first answer',
  takeMe: (name) => `Take me to ${name} plan selection`,
  choose: 'Choose a plan to continue',
  continueWith: (name) => `Continue with ${name}`,
  pricedFor: (band) => `Priced for ${band.replace('-', '–')} employees`,
  // The trial convert actually sent (useTrials), not a fixed 7 (BIO-2158 review).
  perMonth: (days) => `/mo after your ${days}-day trial`,
  afterTrial:
    'Your trial has ended, so this plan starts at your next billing date. Nothing is charged today.',
  changeOfHeart: 'Have a change of heart?',
  fallback:
    'We price plans by company size, and yours needs a quick conversation. Reach your Coverage Advisor by phone or email and we’ll set your plan up together.',
};

// One screen per level: hero, the level's two plans (cheaper first, pre-selected),
// "every plan includes", and the two change-of-heart links to the other levels.
// `includes` is the hero's three; `includesAll` the full block below the cards.
const DEDICATED_TEAM =
  '<b>Dedicated HR Team™:</b> Your Chief of Staff, HR Manager and Staff Coordinator, working inside your business for you.';
const RESOLVED_END_TO_END =
  '<b>HR issues resolved end to end:</b> Your HR Manager resolves every HR issue start to finish and certifies it complete, so you have proof it was done right.';
const MONTHLY_COACHING =
  '<b>Monthly manager coaching sessions:</b> Uncover issues involving your managers and employees for your Dedicated HR Manager to resolve end to end.';
const SMART_HANDBOOK =
  '<b>Smart Handbook™:</b> Installed with every manager and employee, signed by each of them, and kept current as the rules change.';
const DEDICATED_MANAGER =
  '<b>Dedicated HR Manager:</b> Your own Bambee HR Manager, backed by a Bambee HR team, runs your coaching sessions, briefings and follow-through.';
const COACHING =
  '<b>Manager coaching sessions:</b> We meet with your managers, flag problem behavior, and give each manager a step-by-step plan to fix it.';
const CONTINUOUS_COMPLIANCE =
  '<b>Continuous Compliance™:</b> Bambee watches your company’s regulatory environment and tells you what needs attention.';
const GUIDANCE =
  '<b>Guidance on sticky issues:</b> When an issue gets hard, you talk it through with a Bambee expert before you act.';
const YOU_RUN =
  '<b>You run the work:</b> You decide what to do and carry it out yourself, with Bambee guiding you through the hard parts.';

export const PlanLevels = {
  [SupervisionLevel.MANAGEMENT]: {
    name: 'Management',
    kick: 'BAMBEE MANAGEMENT',
    title: 'Great. Bambee <em>Management</em> is perfect for you.',
    sub: 'Your Bambee HR Manager runs every HR issue for you, start to finish. Here’s what every Management plan comes with.',
    includes: [DEDICATED_TEAM, RESOLVED_END_TO_END, MONTHLY_COACHING],
    includesAll: [
      DEDICATED_TEAM,
      RESOLVED_END_TO_END,
      MONTHLY_COACHING,
      SMART_HANDBOOK,
      '<b>Monthly founder briefings:</b> Your Chief of Staff meets with you every month to walk through what’s happening, what’s slipping, and what’s already being handled.',
      '<b>The decisions that define your business stay yours:</b> hiring, structure and investment remain with you.',
    ],
    chooseTitle:
      'Now choose the Management plan that’s right for your business.',
    chooseSub:
      'The difference is whether you have to know what’s wrong, and whether Bambee waits for your go-ahead.',
    cards: [
      {
        productKey: 'WORKFORCE_PRACTICE_MANAGEMENT',
        name: 'Workforce Practice Management',
        tag: 'YOU APPROVE EVERY ISSUE',
        rows: [
          '<b>You define the problem:</b> you tell Bambee what’s wrong, like a leave, a termination or a corrective action, and Bambee takes it from there.',
          '<b>You approve every issue:</b> nothing moves until you say go. Once you do, your HR Manager carries it end to end and certifies it complete.',
        ],
      },
      {
        productKey: 'ORGANIZATIONAL_MATTER_MANAGEMENT',
        name: 'Organizational Matter Management',
        tag: 'BAMBEE RESOLVES IT FOR YOU',
        rows: [
          '<b>Bambee diagnoses the problem:</b> Bambee works out what’s really going on, even when it’s buried or no one can name it, then fixes it.',
          '<b>Bambee resolves it without waiting:</b> Bambee finds the issue and resolves it end to end, without you having to tell us to.',
        ],
      },
    ],
    alternatives: [
      {
        level: SupervisionLevel.SUPERVISION,
        label: 'I want my managers to run it, with Bambee oversight.',
        cta: 'See Supervision plans',
      },
      {
        level: SupervisionLevel.OBSERVATION,
        label: 'I actually want to do HR myself.',
        cta: 'See Observation plans',
      },
    ],
  },
  [SupervisionLevel.SUPERVISION]: {
    name: 'Supervision',
    kick: 'BAMBEE SUPERVISION',
    title: 'Great. Bambee <em>Supervision</em> is perfect for you.',
    sub: 'Your managers do the work, and Bambee makes sure it gets done right. Here’s what every Supervision plan comes with.',
    includes: [DEDICATED_MANAGER, SMART_HANDBOOK, COACHING],
    includesAll: [
      DEDICATED_MANAGER,
      SMART_HANDBOOK,
      COACHING,
      '<b>Follow-through:</b> Your managers do the work, and we hold them accountable to finish it by the next coaching session.',
      '<b>Founder briefings:</b> Your Chief of Staff walks you through what’s happening across your managers and teams, what’s slipping, and what’s already being handled.',
      '<b>Employee feedback:</b> We collect feedback from your employees continuously, so we surface the issues your managers don’t see.',
    ],
    chooseTitle:
      'Now choose the Supervision plan that’s right for your business.',
    chooseSub:
      'The only difference is how often we meet with your team, and with you.',
    cards: [
      {
        productKey: 'STANDARD_SUPERVISION',
        name: 'Standard Supervision',
        tag: 'EVERY QUARTER',
        rows: [
          '<b>4 manager coaching sessions a year:</b> we meet with your managers every quarter to flag problem behavior and set each manager’s step-by-step plan.',
          '<b>4 founder briefings a year:</b> your Chief of Staff walks you through what’s happening across your team every quarter.',
        ],
        why: '<b>Issues caught four times a year:</b> we catch and correct problems at every quarterly session.',
      },
      {
        productKey: 'PRACTICE_SUPERVISION',
        name: 'Practice Supervision',
        tag: 'EVERY 30 DAYS',
        rows: [
          '<b>12 manager coaching sessions a year:</b> we meet with your managers every 30 days to flag problem behavior and set each manager’s step-by-step plan.',
          '<b>12 founder briefings a year:</b> your Chief of Staff walks you through what’s happening across your team every 30 days.',
        ],
        why: '<b>Three times as many sessions:</b> we uncover more, and solve it faster, before it reaches you.',
      },
    ],
    alternatives: [
      {
        level: SupervisionLevel.MANAGEMENT,
        label: 'I want my Bambee HR Manager to do everything for me.',
        cta: 'See Management plans',
      },
      {
        level: SupervisionLevel.OBSERVATION,
        label: 'I actually want to do HR myself.',
        cta: 'See Observation plans',
      },
    ],
  },
  [SupervisionLevel.OBSERVATION]: {
    name: 'Observation',
    kick: 'BAMBEE OBSERVATION',
    title: 'Great. Bambee <em>Observation</em> is perfect for you.',
    sub: 'You run HR yourself. Bambee watches the rules for your company and guides you through the hard issues. Here’s what every Observation plan comes with.',
    includes: [CONTINUOUS_COMPLIANCE, GUIDANCE, YOU_RUN],
    includesAll: [CONTINUOUS_COMPLIANCE, GUIDANCE, YOU_RUN],
    // In the comp, Observation's "includes" block sits below the change-of-heart links.
    includesBelow: true,
    chooseTitle:
      'Now choose the Observation plan that’s right for your business.',
    chooseSub:
      'The difference is whether your workforce is in Bambee, and who guides you through the hard issues.',
    cards: [
      {
        productKey: 'COMPANY_OBSERVATION',
        name: 'Company Observation',
        rows: [
          '<b>A Coverage Advisor hotline:</b> Company Observation doesn’t come with an HR Manager. When a major HR issue comes up, you reach out to your Coverage Advisor, and they talk you through what to do.',
          '<b>A founder-only platform:</b> a simple platform for you to reach out and work with your Coverage Advisor. Your staff aren’t added to Bambee.',
        ],
      },
      {
        productKey: 'WORKFORCE_OBSERVATION',
        name: 'Workforce Observation',
        rows: [
          '<b>A pool of HR Managers:</b> Workforce Observation gives you access to a pool of Bambee HR Managers, who answer your questions and guide you through your sticky HR issues whenever you need one.',
          '<b>Your whole team in Bambee:</b> your managers and employees are added to Bambee, so we can see what’s happening inside your workforce and flag issues early, with Bambee watching the rules in every state you employ in.',
        ],
      },
    ],
    alternatives: [
      {
        level: SupervisionLevel.SUPERVISION,
        label: 'I want Bambee to do more.',
        cta: 'See Supervision plans',
      },
      {
        level: SupervisionLevel.MANAGEMENT,
        label: 'I want my Bambee HR Manager to do everything for me.',
        cta: 'See Management plans',
      },
    ],
  },
};
